import { useCallback, useEffect, useRef, useState } from 'react';
import { buildPdf, pdfBlob } from '../lib/pdfEngine.js';
import { closeNativeSession, nativeJson, nativeRequest } from '../lib/nativeApi.js';

/**
 * A single working document: when native PDF changes succeed, the server PDF is
 * imported back into the same classic pages/history, so split/merge/export and
 * original-object edits never live in two disconnected documents.
 */
export function useNativeWorkspace({ enabled, pages, sourcesRef, documentName, selectedIndex, onPdfChanged }) {
  const [session, setSession] = useState(null);
  const sessionRef = useRef(null);
  const [objects, setObjects] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle');
  const [imagesFirst, setImagesFirst] = useState(false);
  const sessionEpoch = useRef(0);

  useEffect(() => {
    const epoch = ++sessionEpoch.current;
    let cancelled = false;
    let createdId = null;
    const previousId = sessionRef.current;
    sessionRef.current = null;
    setSession(null); setObjects(null); setSelected(null); setDraft(null); setError('');
    if (previousId) void closeNativeSession(previousId);
    if (!enabled || pages.length === 0) { setStatus('idle'); return () => { cancelled = true; }; }
    setStatus('loading');
    (async () => {
      try {
        // Sync ALL displayed pages and browser-added overlays to the PDF engine.
        const bytes = await buildPdf(pages, sourcesRef.current);
        if (cancelled) return;
        const form = new FormData();
        form.append('file', new File([pdfBlob(bytes)], `${documentName || 'Belge'}.pdf`, { type: 'application/pdf' }));
        const response = await nativeJson('/documents', { method: 'POST', body: form });
        createdId = response.id;
        if (cancelled || epoch !== sessionEpoch.current) {
          void closeNativeSession(createdId); return;
        }
        sessionRef.current = createdId;
        setSession(createdId); setStatus('ready');
      } catch (exception) {
        if (!cancelled) { setStatus('error'); setError(exception?.message || String(exception)); }
      }
    })();
    return () => {
      cancelled = true;
      if (createdId) void closeNativeSession(createdId);
    };
  // `pages` is a history snapshot. Changes require recreating the native session.
  }, [enabled, pages, sourcesRef]);

  useEffect(() => {
    if (!session || !enabled || selectedIndex < 0) return;
    let cancelled = false;
    setObjects(null); setSelected(null); setDraft(null);
    nativeJson(`/documents/${session}/pages/${selectedIndex}`)
      .then(data => { if (!cancelled) { setObjects(data); setError(''); } })
      .catch(exception => { if (!cancelled) { setObjects(null); setError(exception?.message || String(exception)); } });
    return () => { cancelled = true; };
  }, [enabled, session, selectedIndex]);

  const choose = useCallback(obj => {
    setSelected(obj);
    setDraft(obj ? {
      text: obj.text || '', font_family: 'original', size: obj.size || 14,
      color: obj.color || '#000000', x: obj.bbox.x, y: obj.bbox.y,
      width: obj.bbox.width, height: obj.bbox.height,
    } : null);
  }, []);

  const mutate = useCallback(async (path, options) => {
    const current = sessionRef.current;
    if (!current || busy) return;
    setBusy(true); setError('');
    try {
      await nativeRequest(`/documents/${current}${path}`, options);
      // Import the actual edited PDF back into the same page editor and history.
      const response = await nativeRequest(`/documents/${current}/download`);
      const file = new File([await response.blob()], `${documentName || 'Belge'}.pdf`, { type: 'application/pdf' });
      await onPdfChanged(file, selectedIndex);
      setSelected(null); setDraft(null);
    } catch (exception) {
      setError(exception?.message || String(exception));
    } finally { setBusy(false); }
  }, [busy, documentName, onPdfChanged, selectedIndex]);

  const editText = useCallback(async () => {
    if (!selected || selected.type !== 'text' || !draft) return;
    await mutate('/text', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: selectedIndex, object_id: selected.id,
        text: draft.text, font_family: draft.font_family, size: Number(draft.size),
        color: draft.color, x: Number(draft.x), y: Number(draft.y),
      }) });
  }, [draft, mutate, selected, selectedIndex]);

  const replaceImage = useCallback(async file => {
    if (!file || !selected || selected.type !== 'image') return;
    const form = new FormData(); form.append('file', file);
    await mutate(`/images/replace?page=${selectedIndex}&object_id=${encodeURIComponent(selected.id)}`,
      { method: 'POST', body: form });
  }, [mutate, selected, selectedIndex]);

  const moveImage = useCallback(async () => {
    if (!selected || selected.type !== 'image' || !draft) return;
    await mutate('/images/move', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: selectedIndex, object_id: selected.id,
        x: Number(draft.x), y: Number(draft.y), width: Number(draft.width), height: Number(draft.height),
      }) });
  }, [draft, mutate, selected, selectedIndex]);

  return { status, busy, error, setError, objects, selected, draft, setDraft,
    choose, editText, replaceImage, moveImage, imagesFirst, setImagesFirst };
}
